import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from "react";
import { StringHelper } from "@bryntum/schedulerpro";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard, createTaskEvent, getScheduleInstanceFromViewManager } from "@/other/utils/otherUtils";
import { swivuGridSorterFn } from "@/other/utils/utils";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { Task } from "@/bryntum-lib/models/Task";
import { getWorkingRangesInScheduler } from "@/other/utils/schedulerUtils";
import TaskDrag from "@/bryntum-lib/widget/extendingClasses/TaskDrag";

interface TaskFinderContextValue {
  tasks: Task[];
  filteredTasks: Task[];
  searchValue: string;
  selectedTasks: Task[];
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchClear: () => void;
  handleTaskSelect: (task: Task) => void;
  handleTaskClick: (task: Task) => void;
  handleTaskDblClick: (task: Task) => void;
  handleClose: () => void;
  handleSubmit: () => void;
  canInsert: boolean;
  canDrag: boolean;
  resourceLabel: string;
  submitButtonText: string;
  showSubmitButton: boolean;
}

interface TaskFinderProviderProps {
  record: {
    onTaskClick?: (params: { record: Task }) => void;
    canInsert?: boolean;
    canDrag?: boolean;
    resourceRecord?: any;
    eventRecord?: any;
    scheduler?: any;
    selectedDate?: Date;
    [key: string]: any;
  };
  onClose: () => void;
  children: React.ReactNode;
}

const TaskFinderContext = createContext<TaskFinderContextValue | undefined>(undefined);

export const useTaskFinderContext = () => {
  const context = useContext(TaskFinderContext);
  if (!context) {
    throw new Error("useTaskFinderContext must be used within TaskFinderProvider");
  }
  return context;
};

export const TaskFinderProvider = ({ record, onClose, children }: TaskFinderProviderProps) => {
  const { project, user, userPreferences, viewManager } = useSwivuBryntumContext();
  const [searchValue, setSearchValue] = useState("");
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const taskDragRef = useRef<TaskDrag | null>(null);

  const { onTaskClick, canInsert = false, canDrag = false, resourceRecord = null, eventRecord = null, scheduler = null, selectedDate = null } = record || {};

  // Get tasks from extraTaskStore
  const tasks = useMemo(() => {
    if (!project?.extraTaskStore) return [];

    const selectedBoard = getSavedBoardSelection();
    const currentBoardUser = project.boardUsersStore?.find((boardUser: any) => boardUser.userId === user?.id);

    let taskRecords = project.extraTaskStore.query((entityRecord: any) => (getIsArchivedForBoard(selectedBoard.slug) || !entityRecord.archived) && !entityRecord.isLinked, true);

    // Filter out the current event's task if replacing
    if (eventRecord?.taskId) {
      taskRecords = taskRecords.filter((record: any) => record.id !== eventRecord.taskId);
    }

    // Prevent duplicate tasks in the same time slot if enabled
    const isEnabledDuplicateRecords = StringHelper.safeJsonParse(userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.enableRedundantTaskEvents, "false") || "false");

    if (!isEnabledDuplicateRecords && canInsert && scheduler && selectedDate && resourceRecord) {
      taskRecords = preventToShowEventsIfDuplicationMatched(taskRecords, scheduler, selectedDate, resourceRecord);
    }

    // Sort tasks: user-updated tasks first (by date), then others alphabetically
    const userUpdatedRecords: Task[] = [];
    const otherRecords: Task[] = [];

    taskRecords.forEach((record: Task) => {
      if (record.lastEventUpdatedBy === currentBoardUser?.id) {
        userUpdatedRecords.push(record);
      } else {
        otherRecords.push(record);
      }
    });

    userUpdatedRecords.sort((a, b) => new Date(b.lastEventUpdatedAt).getTime() - new Date(a.lastEventUpdatedAt).getTime());
    otherRecords.sort(swivuGridSorterFn.bind({ field: "name" }));

    return [...userUpdatedRecords, ...otherRecords];
  }, [project, user, eventRecord, canInsert, scheduler, selectedDate, resourceRecord, userPreferences]);

  // Filter tasks based on search
  const filteredTasks = useMemo(() => {
    if (!searchValue) return tasks;
    const lowerSearch = searchValue.toLowerCase();
    return tasks.filter((task) => (task.name || "").toLowerCase().includes(lowerSearch));
  }, [tasks, searchValue]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleTaskSelect = useCallback(
    (task: Task) => {
      setSelectedTasks((prev) => {
        const isSelected = prev.some((t) => t.id === task.id);
        if (isSelected) {
          return prev.filter((t) => t.id !== task.id);
        } else {
          return canInsert ? [...prev, task] : [task];
        }
      });
    },
    [canInsert]
  );

  const handleTaskClick = useCallback(
    (task: Task) => {
      if (onTaskClick) {
        onTaskClick({ record: task });
        onClose();
      }
    },
    [onTaskClick, onClose]
  );

  const handleTaskDblClick = useCallback(
    (task: Task) => {
      if (onTaskClick) {
        onTaskClick({ record: task });
        setTimeout(() => onClose(), 100);
      }
    },
    [onTaskClick, onClose]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!canInsert || selectedTasks.length === 0) return;

    if (eventRecord) {
      // Replace existing event
      eventRecord.remove();
      selectedTasks.forEach((task) => {
        createTaskEvent({
          schedule: scheduler,
          task,
          resource: resourceRecord,
          date: selectedDate,
          eventStartDate: eventRecord.startDate,
          eventEndDate: eventRecord.endDate
        });
      });
    } else {
      // Insert new events
      const eventsToAdd: any[] = [];
      const assignmentsToAdd: any[] = [];

      const state = scheduler.project.crudStores.map((s: any) => {
        const state = s.store.reapplyFilterOnUpdate;
        s.store.reapplyFilterOnUpdate = false;
        return state;
      });

      const promise = scheduler.project.commitAsync();

      getWorkingRangesInScheduler(selectedDate, scheduler).forEach((dateRange: any) => {
        selectedTasks.forEach((task) => {
          const newEvent = scheduler.eventStore.createRecord({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            taskId: task.id
          });
          const newAssignment = scheduler.assignmentStore.createRecord({
            resourceId: resourceRecord.id,
            eventId: newEvent.id
          });
          eventsToAdd.push(newEvent);
          assignmentsToAdd.push(newAssignment);
        });
      });

      scheduler.eventStore.add(eventsToAdd);
      scheduler.assignmentStore.add(assignmentsToAdd);

      await promise;

      scheduler.project.crudStores.forEach((s: any, index: number) => {
        s.store.reapplyFilterOnUpdate = state[index];
        s.store.filter();
      });
    }

    onClose();
  }, [canInsert, selectedTasks, eventRecord, scheduler, resourceRecord, selectedDate, onClose]);

  // Generate resource label and submit button text
  const resourceLabel = useMemo(() => {
    if (!canInsert) return "";

    if (eventRecord) {
      return `Replace ${eventRecord.task?.name || "N/A"} in row ${resourceRecord?.name || "N/A"}`;
    } else {
      const dateStr = selectedDate
        ? new Date(selectedDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
          })
        : "N/A";
      return `Insert into row ${resourceRecord?.name || "N/A"} on ${dateStr}`;
    }
  }, [canInsert, eventRecord, resourceRecord, selectedDate]);

  const submitButtonText = useMemo(() => {
    return eventRecord ? "Replace" : "Insert";
  }, [eventRecord]);

  const showSubmitButton = canInsert;

  useEffect(() => {
    if (!taskDragRef.current && viewManager && canDrag) {
      if (viewManager.pageViewsRef) {
        const schedularInstance = getScheduleInstanceFromViewManager(viewManager);
        if (schedularInstance) {
          taskDragRef.current = new TaskDrag({
            // @ts-expect-error getRecordFromElement is not in the type definition
            grid: {
              getRecordFromElement: (el: any) => {
                const taskId = el.dataset.swivuTaskId;
                return project.extraTaskStore.getById(taskId);
              }
            },
            schedule: schedularInstance,
            constrain: false,
            targetSelector: ".swivu-task-finder-drag-handler"
          });
        }
      }
    }

    return () => {
      if (taskDragRef.current) {
        taskDragRef.current?.destroy?.();
        taskDragRef.current = null;
      }
    };
  }, [viewManager, project, canDrag]);

  const value: TaskFinderContextValue = {
    tasks,
    filteredTasks,
    searchValue,
    selectedTasks,
    handleSearchChange,
    handleSearchClear,
    handleTaskSelect,
    handleTaskClick,
    handleTaskDblClick,
    handleClose,
    handleSubmit,
    canInsert,
    canDrag,
    resourceLabel,
    submitButtonText,
    showSubmitButton
  };

  return <TaskFinderContext.Provider value={value}>{children}</TaskFinderContext.Provider>;
};

// Helper function to prevent showing duplicate events in the same time slot
function preventToShowEventsIfDuplicationMatched(records: Task[], scheduler: any, selectedDate: Date, resourceRecord: any): Task[] {
  const tickIndex = scheduler.timeAxis.getTickFromDate(selectedDate);
  const selectedTick = scheduler.timeAxis.getAt(Math.floor(tickIndex));
  const tickStartDate = selectedTick.startDate ?? new Date(selectedTick.start);
  const tickEndDate = selectedTick.endDate ?? new Date(selectedTick.end);

  const currentSelectedTimeRangeRecord = scheduler.eventStore.getEvents({
    startDate: tickStartDate,
    endDate: tickEndDate,
    resourceRecord: resourceRecord
  });

  return records.filter((record) => {
    return !currentSelectedTimeRangeRecord.some((event: any) => event.taskId === record.id);
  });
}
